//* 组件库
import { $message, utility, $loading } from "various-ui";
//* 公共组件
import type { ConfirmModal } from "@/components/windows";
//* 公共函数
import { Formats } from "@/assets/js";
//* 私有组件
import Gene from "../components/gene/index.vue";
import Protein from "../components/protein/index.vue";
import Transcript from "../components/transcript/index.vue";
import PreviewModel from "../components/preview/index.vue";
//* 组件属性
import type { VPMInitOption, VPMFormData } from "../index";

export const useComposable = () => {
    //* 获取i18n
    const i18n = useI18n();

    //* 获取Route
    const route = useRoute();

    //* 响应式变量
    const refs = {
        mrnas: ref<any[]>([]),
        sequence: ref<string>(""),
        dictionaries: ref<{ [name: string]: any }>({}),
    };

    //* 表单数据
    const data = reactive<VPMFormData>({
        id: Formats.query(route.query.id), //? 基因ID
        mode: "Gene", //? 当前模式
        name: "", //? 基因名称
        mrna: null, //? 当前选择的mrna
        paste: "", //? 待解析的字符串
        transcript: "", //? 转录本ID
    });

    //* 节点
    const nodes = {
        model: ref<InstanceType<typeof Gene>>(),
        confirm: ref<InstanceType<typeof ConfirmModal>>(),
        preview: ref<InstanceType<typeof PreviewModel>>(),
    };

    //* 工具函数
    const utils = {
        //* 数据处理函数
        data: (response: any) => {
            if (response?.data?.length) {
                //* 数据初始化
                data.name = response.name || "--";
                refs.mrnas.value = [];
                refs.sequence.value = response.sequence || "";
                //* 生成转录本选择器配置项
                response?.data?.forEach((mrna: any) => {
                    refs.mrnas.value.push(Object.assign(mrna, { length: refs.sequence.value.length }));
                });

                //* 初始化选择器数据
                data.mrna = refs.mrnas.value.find((value) => value.value == data.transcript) || refs.mrnas.value[0];
                data.transcript = data.mrna.value;

                //* 处理数据生成蛋白列表与CDS列表
                utils.dictionaries();

                //* 初始化输入模型与预览模型
                nextTick(() => {
                    nodes.model.value?.init(data, Object.assign(refs.dictionaries.value, { sequence: refs.sequence.value }));
                    nodes.preview.value?.init(data.mrna);
                    if (data.paste) {
                        methods.mutation();
                    }
                });
            } else {
                $message.warning(i18n.t("Placeholder.GeneNotTranscript"));
            }
        },

        //* 生成CDS字典与蛋白字典
        dictionaries: () => {
            //* 初始化蛋白计数器
            const config: any = { start: 0, end: 0, chars: [] };

            //* 初始化字典
            refs.dictionaries.value.cds = [];
            refs.dictionaries.value.protein = [];

            //- 3. 遍历CDS列表生成CDS字典与蛋白字典
            data.mrna.cds.forEach((value: any) => {
                for (let i = value[0]; i <= value[1]; i++) {
                    const char = refs.sequence.value.charAt(i - 1);
                    refs.dictionaries.value.cds.push({ start: i, value: char });
                    if (config.chars.length == 3) {
                        refs.dictionaries.value.protein.push(utility.cloneDeep(Object.assign(config, { value: config.chars.join("") })));
                        config.start = i;
                        config.chars = [char];
                    } else {
                        config.chars.push(char);
                        config.start = config.start || i;
                        config.end = i;
                    }
                }
            });

            //- 4. 检测是否存在蛋白
            if (config.chars.length == 3) {
                refs.dictionaries.value.protein.push(utility.cloneDeep(Object.assign(config, { value: config.chars.join("") })));
            }
        },

        //* 表单提交函数
        submit: (data: Object) => {
            $loading.open({ message: i18n.t("UnderLoad") });
            api.frontend("/api/ai/mouse-pheno-predict", {
                data: data,
                method: "POST",
                success: (response) => {
                    if (response) {
                        window.open(routerResolve({ name: "MousePhenoPredictResult", params: { id: response } }).href as string, "_blank");
                    } else {
                        $message.warning(i18n.t("UnknownError"));
                    }
                },
                finally: () => {
                    $loading.close();
                },
            });
        },
    };

    //* 函数列表
    const methods = {
        //* 初始化函数
        init: (option: VPMInitOption) => {
            //* 开启加载器
            $loading.open({ message: i18n.t("UnderLoad"), delay: 200 });

            //* 初始化数据
            data.id = option.id;
            data.paste = option.nucleotide || "";
            data.transcript = option.transcript || "";
            //* 数据获取
            api.frontend("/api/gene/mrnas-info", {
                data: { id: data.id },
                finally: () => $loading.close(),
                success: (response) => {
                    utils.data(response);
                },
            });
        },

        //* 切换Mrna
        mrna: () => {
            const mrna = refs.mrnas.value.find((val) => val.value === data.transcript);
            //* 获取Mrna成功时触发输入模型的初始化函数
            if (mrna) {
                data.mrna = mrna;
                data.paste = "";
                utils.dictionaries();
                //* 初始化输入模型与预览模型
                nextTick(() => {
                    nodes.model.value?.init(data, Object.assign(refs.dictionaries.value, { sequence: refs.sequence.value }));
                    nodes.preview.value?.init(data.mrna);
                });
            }
        },

        //* 切换模式
        mode: function () {
            nextTick(() => {
                //* 数据更新
                data.paste = "";
                //* 可视化模型更新
                nodes.model.value?.init(data, Object.assign(refs.dictionaries.value, { sequence: refs.sequence.value }));
                nodes.preview.value?.init(data.mrna);
            });
        },

        //* 切换基因
        change: (response: any) => {
            //* 路由更新
            routerPush({ name: "MousePhenoPredict", query: { id: response.id } });
            //* 数据更新
            methods.init({ id: response.id });
        },

        //* 表单提交
        submit: function () {
            nodes.model.value?.submit((res: any) => {
                const result = Object.assign(res, { name: data.name, gene_id: data.id, transcript_id: data.transcript });
                if (!result.alt) nodes.confirm.value?.open(result);
                else {
                    utils.submit(result);
                }
            });
        },

        //* 解析突变信息
        mutation: function () {
            //* 无效数据检测
            if (!data.paste) return;
            //* 发起请求
            $loading.open({ message: "", delay: 200 });
            //* 突变位点解析
            api.frontend("/api/mutations/analyze", {
                data: { id: data.id, keyword: data.paste, transcript: data.transcript },
                method: "POST",
                finally: () => $loading.close(),
                success: (response) => {
                    if (data.mode == "Gene") {
                        //* 当前模式为Gene模式时直接切换表格数据
                        nodes.model.value?.changeData(response);
                    } else {
                        //* 反之切换模式并初始化预览模型和切换表格数据
                        data.mode = "Gene";
                        nextTick(() => {
                            nodes.model.value?.init(data, Object.assign(refs.dictionaries.value, { sequence: refs.sequence.value }));
                            nodes.model.value?.changeData(response);
                            nodes.preview.value?.init(data.mrna);
                        });
                    }
                },
            });
        },
    };

    //* 计算属性
    const computeds = {
        view: computed(() => {
            switch (data.mode) {
                case "Protein":
                    return Protein;
                case "Transcript":
                    return Transcript;
                default:
                    return Gene;
            }
        }),
    };

    return { data, refs, nodes, utils, methods, computeds };
};
